import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  FiAward,
  FiCalendar,
  FiEdit3,
  FiList,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUserPlus,
  FiUsers,
  FiX,
} from "react-icons/fi";
import {
  OFFICE_COMPETITION_GROUPS,
  addOfficeCompetitionMember,
  deleteOfficeCompetitionKpi,
  fetchOfficeCompetitionDashboard,
  fetchOfficeCompetitionGroupOrderDetail,
  fetchOfficeCompetitionGroupOrders,
  removeOfficeCompetitionMember,
  saveOfficeCompetitionKpi,
  searchOfficeCompetitionEmployees,
} from "../../services/officeCompetitionService";
import NavbarTrungThu from "../../components/Layout/NavbarTrungThu";
import "./OfficeCompetitionPage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const MILESTONES = [
  { key: "basic", label: "Cơ bản", color: "#94a3b8" },
  { key: "accelerate", label: "Tăng tốc", color: "#38bdf8" },
  { key: "leading", label: "Dẫn đầu", color: "#8b5cf6" },
  { key: "wow", label: "WOW", color: "#f97316" },
];

const GROUP_COLORS = ["#2563eb", "#7c3aed", "#ea580c", "#059669"];
const LEVEL_COLORS = {
  below: "#ef4444",
  none: "#cbd5e1",
};
const CHART_LEVELS = [
  { key: "below", label: "Chưa đạt Cơ bản", color: LEVEL_COLORS.below },
  ...MILESTONES,
  { key: "none", label: "Chưa đặt KPI", color: LEVEL_COLORS.none },
];
const numberFormatter = new Intl.NumberFormat("vi-VN", {
  maximumFractionDigits: 0,
});

const DEFAULT_FILTERS = {
  fromDate: "2026-07-01",
  toDate: "2026-11-01",
};

const emptyMilestones = () => ({
  basic: "",
  accelerate: "",
  leading: "",
  wow: "",
});

const parseEmployeeCodes = (value) => [
  ...new Set(
    String(value || "")
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean),
  ),
];

const getKpiLevel = (actual, milestones) => {
  if (!milestones) return { label: "Chưa đặt KPI", key: "none" };

  const value = Number(actual) || 0;
  if (value >= Number(milestones.wow)) return { label: "WOW", key: "wow" };
  if (value >= Number(milestones.leading)) {
    return { label: "Dẫn đầu", key: "leading" };
  }
  if (value >= Number(milestones.accelerate)) {
    return { label: "Tăng tốc", key: "accelerate" };
  }
  if (value >= Number(milestones.basic)) {
    return { label: "Cơ bản", key: "basic" };
  }
  return { label: "Chưa đạt Cơ bản", key: "below" };
};

const getKpiLevelColor = (levelKey) =>
  CHART_LEVELS.find((level) => level.key === levelKey)?.color ||
  LEVEL_COLORS.none;

const getNextKpiMilestone = (actual, milestones) => {
  if (!milestones) return null;

  const value = Number(actual) || 0;
  const nextMilestone = MILESTONES.find(
    (milestone) => value < Number(milestones[milestone.key]),
  );
  if (!nextMilestone) return null;

  const target = Number(milestones[nextMilestone.key]) || 0;
  return {
    ...nextMilestone,
    remaining: Math.max(target - value, 0),
  };
};

const mergeFixedGroups = (groups = []) => {
  const groupMap = new Map(
    groups.map((group) => [String(group.groupId), group]),
  );

  return OFFICE_COMPETITION_GROUPS.map((fixedGroup) => ({
    ...fixedGroup,
    actual: { cakeQuantity: 0, wowOrderCount: 0 },
    kpi: null,
    members: [],
    ...(groupMap.get(fixedGroup.groupId) || {}),
    groupId: fixedGroup.groupId,
    groupName: fixedGroup.groupName,
  }));
};

const valueAndLevelPlugin = {
  id: "officeCompetitionValueAndLevel",
  afterDatasetsDraw(chart, args, options) {
    const actualMeta = chart.getDatasetMeta(0);
    const values = chart.data.datasets[0]?.data || [];
    const levels = options?.levels || [];
    const { ctx } = chart;

    ctx.save();
    ctx.textAlign = "center";
    actualMeta.data.forEach((bar, index) => {
      if (values[index] === null || values[index] === undefined) return;
      const top = Math.max(bar.y - 24, chart.chartArea.top + 10);
      ctx.fillStyle = "#0f172a";
      ctx.font = "700 11px sans-serif";
      ctx.fillText(numberFormatter.format(Number(values[index]) || 0), bar.x, top);
      ctx.fillStyle = "#64748b";
      ctx.font = "600 10px sans-serif";
      ctx.fillText(levels[index] || "", bar.x, top + 13);
    });
    ctx.restore();
  },
};

const Modal = ({ title, children, onClose, labelledBy, wide = false }) => (
  <div className="oc-modal-backdrop" role="presentation" onMouseDown={onClose}>
    <section
      className={`oc-modal ${wide ? "oc-modal-wide" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <header className="oc-modal-header">
        <h2 id={labelledBy}>{title}</h2>
        <button className="oc-icon-button" onClick={onClose} aria-label="Đóng">
          <FiX />
        </button>
      </header>
      {children}
    </section>
  </div>
);

const OfficeCompetitionPage = ({ showNavbarTrungThu = false }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [kpiGroup, setKpiGroup] = useState(null);
  const [memberGroup, setMemberGroup] = useState(null);
  const [orderListGroup, setOrderListGroup] = useState(null);
  const [orderListData, setOrderListData] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetailError, setOrderDetailError] = useState("");
  const [kpiForm, setKpiForm] = useState({
    fromDate: DEFAULT_FILTERS.fromDate,
    toDate: DEFAULT_FILTERS.toDate,
    cakeQuantity: emptyMilestones(),
    wowOrderCount: emptyMilestones(),
  });
  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeResults, setEmployeeResults] = useState([]);
  const [hasSearchedEmployee, setHasSearchedEmployee] = useState(false);
  const [modalError, setModalError] = useState("");
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  const loadDashboard = useCallback(async (nextFilters = appliedFilters) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchOfficeCompetitionDashboard(nextFilters);
      setDashboard(response?.data || null);
    } catch (requestError) {
      setError(requestError?.message || "Không thể tải dữ liệu thi đua.");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    loadDashboard(appliedFilters);
  }, [appliedFilters, loadDashboard]);

  const groups = useMemo(
    () => mergeFixedGroups(dashboard?.groups),
    [dashboard?.groups],
  );
  const managementGroups = useMemo(
    () => [...groups].sort((left, right) => Number(left.groupId === "0") - Number(right.groupId === "0")),
    [groups],
  );
  const enteredEmployeeCodes = useMemo(
    () => parseEmployeeCodes(employeeCode),
    [employeeCode],
  );

  const buildChartData = (metricKey) => ({
    labels: groups.map((group) =>
      group.groupId === "0"
        ? ["0 - CKSH", "NVKD Đơn Hàng Sỉ"]
        : `${group.groupId} - ${group.groupName}`,
    ),
    datasets: [
      {
        label: "Thực tế",
        data: groups.map((group) => Number(group.actual?.[metricKey]) || 0),
        backgroundColor: groups.map((group) =>
          getKpiLevelColor(
            getKpiLevel(
              group.actual?.[metricKey],
              group.kpi?.[metricKey],
            ).key,
          ),
        ),
        borderRadius: 7,
        maxBarThickness: 56,
      },
    ],
  });

  const buildChartOptions = (metricKey) => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 32 } },
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        onClick: () => {},
        labels: {
          boxWidth: 11,
          boxHeight: 11,
          usePointStyle: true,
          generateLabels: () =>
            CHART_LEVELS.map((level, index) => ({
              text: level.label,
              fillStyle: level.color,
              strokeStyle: level.color,
              hidden: false,
              index,
            })),
        },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${numberFormatter.format(context.raw || 0)}`,
          afterTitle: (items) => {
            const index = items[0]?.dataIndex;
            const group = groups[index];
            return `Cấp hiện tại: ${getKpiLevel(
              group?.actual?.[metricKey],
              group?.kpi?.[metricKey],
            ).label}`;
          },
          afterBody: (items) => {
            const index = items[0]?.dataIndex;
            const group = groups[index];
            const milestones = group?.kpi?.[metricKey];
            if (!milestones) return "Chưa đặt KPI.";

            const nextMilestone = getNextKpiMilestone(
              group?.actual?.[metricKey],
              milestones,
            );
            if (!nextMilestone) return "Đã đạt mốc WOW cao nhất.";

            const unit = metricKey === "cakeQuantity" ? "bánh" : "đơn";
            return `Còn ${numberFormatter.format(nextMilestone.remaining)} ${unit} để đạt ${nextMilestone.label}.`;
          },
        },
      },
      officeCompetitionValueAndLevel: {
        levels: groups.map(
          (group) =>
            `Cấp: ${getKpiLevel(
              group.actual?.[metricKey],
              group.kpi?.[metricKey],
            ).label}`,
        ),
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#475569", font: { weight: "600" } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(148, 163, 184, 0.18)" },
        ticks: {
          precision: 0,
          callback: (value) => numberFormatter.format(value),
        },
      },
    },
  });

  const applyFilters = (event) => {
    event.preventDefault();
    setNotice("");
    if (!filters.fromDate || !filters.toDate || filters.fromDate > filters.toDate) {
      setError("Khoảng ngày không hợp lệ.");
      return;
    }
    setAppliedFilters({ ...filters });
  };

  const openKpiModal = (group) => {
    const toFormMilestones = (milestones) =>
      MILESTONES.reduce(
        (result, milestone) => ({
          ...result,
          [milestone.key]: milestones?.[milestone.key]
            ? String(milestones[milestone.key])
            : "",
        }),
        {},
      );

    setModalError("");
    setKpiGroup(group);
    setKpiForm({
      fromDate: group.kpi?.fromDate || appliedFilters.fromDate,
      toDate: group.kpi?.toDate || appliedFilters.toDate,
      cakeQuantity: toFormMilestones(group.kpi?.cakeQuantity),
      wowOrderCount: toFormMilestones(group.kpi?.wowOrderCount),
    });
  };

  const updateKpiMilestone = (metricKey, milestoneKey, value) => {
    setKpiForm((current) => ({
      ...current,
      [metricKey]: {
        ...current[metricKey],
        [milestoneKey]: value,
      },
    }));
  };

  const validateMilestones = (label, milestones) => {
    const values = MILESTONES.map((milestone) => Number(milestones[milestone.key]));
    if (values.some((value) => !Number.isInteger(value) || value <= 0)) {
      return `${label}: cả 4 mốc phải là số nguyên lớn hơn 0.`;
    }
    if (values.some((value, index) => index > 0 && value <= values[index - 1])) {
      return `${label}: phải tăng dần theo Cơ bản < Tăng tốc < Dẫn đầu < WOW.`;
    }
    return "";
  };

  const handleSaveKpi = async (event) => {
    event.preventDefault();
    const milestoneError =
      validateMilestones("Số lượng bánh", kpiForm.cakeQuantity) ||
      validateMilestones("Số đơn WOW", kpiForm.wowOrderCount);
    if (milestoneError) {
      setModalError(milestoneError);
      return;
    }
    if (
      !kpiForm.fromDate ||
      !kpiForm.toDate ||
      kpiForm.fromDate > kpiForm.toDate
    ) {
      setModalError("Khoảng áp dụng KPI không hợp lệ.");
      return;
    }

    const toNumberMilestones = (milestones) =>
      MILESTONES.reduce(
        (result, milestone) => ({
          ...result,
          [milestone.key]: Number(milestones[milestone.key]),
        }),
        {},
      );

    setSaving(true);
    setModalError("");
    try {
      const response = await saveOfficeCompetitionKpi(kpiGroup.groupId, {
        fromDate: kpiForm.fromDate,
        toDate: kpiForm.toDate,
        cakeQuantity: toNumberMilestones(kpiForm.cakeQuantity),
        wowOrderCount: toNumberMilestones(kpiForm.wowOrderCount),
      });
      setKpiGroup(null);
      setNotice(response?.message || "Đã lưu KPI.");
      await loadDashboard(appliedFilters);
    } catch (requestError) {
      setModalError(requestError?.message || "Không thể lưu KPI.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKpi = async () => {
    if (!kpiGroup?.kpi) return;
    if (!window.confirm(`Xóa toàn bộ KPI của ${kpiGroup.groupName} trong kỳ này?`)) {
      return;
    }
    setSaving(true);
    setModalError("");
    try {
      const response = await deleteOfficeCompetitionKpi(kpiGroup.groupId, {
        fromDate: kpiGroup.kpi.fromDate,
        toDate: kpiGroup.kpi.toDate,
      });
      setKpiGroup(null);
      setNotice(response?.message || "Đã xóa KPI.");
      await loadDashboard(appliedFilters);
    } catch (requestError) {
      setModalError(requestError?.message || "Không thể xóa KPI.");
    } finally {
      setSaving(false);
    }
  };

  const openMemberModal = (group) => {
    setMemberGroup(group);
    setEmployeeCode("");
    setEmployeeResults([]);
    setHasSearchedEmployee(false);
    setModalError("");
  };

  const openOrderListModal = async (group) => {
    setOrderListGroup(group);
    setOrderListData(null);
    setOrdersError("");
    setSelectedOrder(null);
    setOrdersLoading(true);
    try {
      const response = await fetchOfficeCompetitionGroupOrders(
        group.groupId,
        appliedFilters,
      );
      setOrderListData(response?.data || null);
    } catch (requestError) {
      setOrdersError(requestError?.message || "Không thể tải danh sách đơn.");
    } finally {
      setOrdersLoading(false);
    }
  };

  const openOrderDetailModal = async (order) => {
    setSelectedOrder(order);
    setOrderDetail(null);
    setOrderDetailError("");
    setOrderDetailLoading(true);
    try {
      const response = await fetchOfficeCompetitionGroupOrderDetail(
        orderListGroup.groupId,
        order.orderId,
        appliedFilters,
      );
      setOrderDetail(response?.data || null);
    } catch (requestError) {
      setOrderDetailError(requestError?.message || "Không thể tải chi tiết đơn.");
    } finally {
      setOrderDetailLoading(false);
    }
  };

  const handleSearchEmployee = async (event) => {
    event.preventDefault();
    if (enteredEmployeeCodes.length === 0) {
      setModalError("Vui lòng nhập mã nhân viên.");
      return;
    }
    if (enteredEmployeeCodes.length > 1) {
      await handleAddMembers(enteredEmployeeCodes);
      return;
    }

    setSearching(true);
    setHasSearchedEmployee(false);
    setModalError("");
    try {
      const response = await searchOfficeCompetitionEmployees(
        enteredEmployeeCodes[0],
      );
      setEmployeeResults(response?.data || []);
      setHasSearchedEmployee(true);
    } catch (requestError) {
      setModalError(requestError?.message || "Không thể tìm nhân viên.");
    } finally {
      setSearching(false);
    }
  };

  const refreshMemberGroup = async () => {
    const response = await fetchOfficeCompetitionDashboard(appliedFilters);
    setDashboard(response?.data || null);
    const refreshedGroup = mergeFixedGroups(response?.data?.groups).find(
      (group) => group.groupId === memberGroup.groupId,
    );
    setMemberGroup(refreshedGroup || null);
  };

  const handleAddMembers = async (employeeCodes) => {
    setSaving(true);
    setModalError("");
    try {
      const response = await addOfficeCompetitionMember(
        memberGroup.groupId,
        employeeCodes,
      );
      setNotice(response?.message || "Đã cập nhật thành viên.");
      await refreshMemberGroup();
      setEmployeeResults([]);
      setEmployeeCode("");
      setHasSearchedEmployee(false);
    } catch (requestError) {
      setModalError(requestError?.message || "Không thể cập nhật thành viên.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = (employee) =>
    handleAddMembers(employee.employeeCode);

  const handleRemoveMember = async (employeeCodeToRemove) => {
    if (
      !window.confirm(
        `Gỡ nhân viên ${employeeCodeToRemove} khỏi ${memberGroup.groupName}?`,
      )
    ) {
      return;
    }
    setSaving(true);
    setModalError("");
    try {
      const response = await removeOfficeCompetitionMember(
        memberGroup.groupId,
        employeeCodeToRemove,
      );
      setNotice(response?.message || "Đã gỡ thành viên.");
      await refreshMemberGroup();
    } catch (requestError) {
      setModalError(requestError?.message || "Không thể gỡ thành viên.");
    } finally {
      setSaving(false);
    }
  };

  const renderMetricProgress = (group, metricKey, label, unit) => {
    const actual = Number(group.actual?.[metricKey]) || 0;
    const milestones = group.kpi?.[metricKey];
    const level = getKpiLevel(actual, milestones);
    const progress = milestones ? (actual / Number(milestones.wow)) * 100 : 0;

    return (
      <div className="oc-metric-progress">
        <div className="oc-progress-meta">
          <span>{label}</span>
          <strong>{numberFormatter.format(actual)} {unit}</strong>
        </div>
        <span className={`oc-level-badge oc-level-${level.key}`}>{level.label}</span>
        {milestones && (
          <>
            <div
              className="oc-progress-track"
              role="progressbar"
              aria-label={`Tiến độ ${label} ${group.groupName}`}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={Math.min(Math.round(progress), 100)}
            >
              <span
                style={{
                  width: `${Math.min(Math.max(progress, 0), 100)}%`,
                  backgroundColor: GROUP_COLORS[Number(group.groupId)],
                }}
              />
            </div>
            <div className="oc-milestone-caption">
              {MILESTONES.map(
                (milestone) =>
                  `${milestone.label}: ${numberFormatter.format(milestones[milestone.key])}`,
              ).join(" · ")}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {showNavbarTrungThu && <NavbarTrungThu />}
      <main className="oc-page">
      <header className="oc-page-header">
        <div>
          <span className="oc-eyebrow"><FiAward /> Thi đua văn phòng</span>
          <h1>Biểu đồ thi đua giữa các nhóm</h1>
          <p>Theo dõi số lượng bánh và số đơn WOW có trên 100 bánh/đơn.</p>
        </div>
      </header>

      <form className="oc-filter" onSubmit={applyFilters}>
        <label>
          <span><FiCalendar /> Từ ngày</span>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                fromDate: event.target.value,
              }))
            }
          />
        </label>
        <label>
          <span><FiCalendar /> Đến ngày</span>
          <input
            type="date"
            value={filters.toDate}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                toDate: event.target.value,
              }))
            }
          />
        </label>
        <button className="oc-primary-button" type="submit" disabled={loading}>
          <FiRefreshCw className={loading ? "oc-spin" : ""} /> Xem dữ liệu
        </button>
      </form>

      {error && <div className="oc-alert oc-alert-error" role="alert">{error}</div>}
      {notice && <div className="oc-alert oc-alert-success" role="status">{notice}</div>}

      <section className="oc-kpi-overview" aria-labelledby="oc-overview-title">
        <div className="oc-section-heading">
          <h2 id="oc-overview-title">KPI tổng quan 4 nhóm</h2>
          <p>Số thực tế và cấp KPI hiện tại của từng tiêu chí.</p>
        </div>
        <div className="oc-summary-grid">
          {groups.map((group) => {
            const cakeLevel = getKpiLevel(
              group.actual?.cakeQuantity,
              group.kpi?.cakeQuantity,
            );
            const wowLevel = getKpiLevel(
              group.actual?.wowOrderCount,
              group.kpi?.wowOrderCount,
            );
            return (
              <article
                className="oc-summary-card"
                key={group.groupId}
                aria-label={`KPI tổng quan ${group.groupName}`}
              >
                <div className="oc-summary-card-header">
                  <span>Nhóm {group.groupId}</span>
                  <strong>{group.groupName}</strong>
                </div>
                <div className="oc-summary-metrics">
                  <div>
                    <span>Số bánh</span>
                    <strong>{numberFormatter.format(group.actual?.cakeQuantity || 0)}</strong>
                    <small className={`oc-level-badge oc-level-${cakeLevel.key}`}>{cakeLevel.label}</small>
                  </div>
                  <div>
                    <span>Đơn WOW</span>
                    <strong>{numberFormatter.format(group.actual?.wowOrderCount || 0)}</strong>
                    <small className={`oc-level-badge oc-level-${wowLevel.key}`}>{wowLevel.label}</small>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="oc-charts-grid" aria-label="Biểu đồ KPI thi đua">
        <article className="oc-chart-card">
          <div className="oc-section-heading">
            <h2>Số lượng bánh</h2>
            <p>Mỗi nhóm là một cột, màu cột thể hiện mốc KPI hiện tại.</p>
          </div>
          <div className="oc-chart-wrap">
            {loading ? (
              <div className="oc-loading" role="status">
                <FiRefreshCw className="oc-spin" /> Đang tải dữ liệu...
              </div>
            ) : (
              <Bar
                data={buildChartData("cakeQuantity")}
                options={buildChartOptions("cakeQuantity")}
                plugins={[valueAndLevelPlugin]}
              />
            )}
          </div>
        </article>

        <article className="oc-chart-card">
          <div className="oc-section-heading">
            <h2>Số đơn WOW</h2>
            <p>Mỗi nhóm là một cột, màu cột thể hiện mốc KPI hiện tại.</p>
          </div>
          <div className="oc-chart-wrap">
            {loading ? (
              <div className="oc-loading" role="status">
                <FiRefreshCw className="oc-spin" /> Đang tải dữ liệu...
              </div>
            ) : (
              <Bar
                data={buildChartData("wowOrderCount")}
                options={buildChartOptions("wowOrderCount")}
                plugins={[valueAndLevelPlugin]}
              />
            )}
          </div>
        </article>
      </section>

      <section className="oc-groups-grid" aria-label="Quản lý nhóm thi đua">
        {managementGroups.map((group) => (
          <article
            className={`oc-group-card ${group.groupId === "0" ? "oc-group-card--group-zero" : ""}`}
            key={group.groupId}
            aria-label={`Thẻ thi đua ${group.groupName}`}
          >
            <div
              className="oc-group-accent"
              style={{ backgroundColor: GROUP_COLORS[Number(group.groupId)] }}
            />
            <header>
              <span className="oc-group-code">Nhóm {group.groupId}</span>
              <h3>{group.groupName}</h3>
            </header>

            {group.kpi ? (
              <div className="oc-group-metrics">
                {renderMetricProgress(group, "cakeQuantity", "Số bánh", "bánh")}
                {renderMetricProgress(group, "wowOrderCount", "Đơn WOW", "đơn")}
              </div>
            ) : (
              <div className="oc-no-kpi">Chưa đặt KPI · Tiến độ được ẩn</div>
            )}

            <div className="oc-member-preview">
              <FiUsers />
              <span>{group.members?.length || 0} thành viên</span>
              <small>
                {group.members?.slice(0, 2).map((member) => member.employeeCode).join(", ") || "Chưa có thành viên"}
              </small>
            </div>
            <footer>
              <button className="oc-secondary-button" onClick={() => openKpiModal(group)}>
                <FiEdit3 /> {group.kpi ? "Sửa KPI" : "Đặt KPI"}
              </button>
              <button className="oc-secondary-button" onClick={() => openMemberModal(group)}>
                <FiUserPlus /> Thành viên
              </button>
              <button
                className="oc-secondary-button"
                type="button"
                onClick={() => openOrderListModal(group)}
              >
                <FiList /> Danh sách đơn
              </button>
            </footer>
          </article>
        ))}
      </section>

      {orderListGroup && !selectedOrder && (
        <Modal
          title={`Danh sách đơn · ${orderListGroup.groupId} - ${orderListGroup.groupName}`}
          labelledBy="oc-order-list-modal-title"
          onClose={() => !ordersLoading && setOrderListGroup(null)}
          wide
        >
          <div className="oc-modal-body">
            {ordersLoading && (
              <div className="oc-loading oc-order-loading" role="status">
                <FiRefreshCw className="oc-spin" /> Đang tải danh sách đơn...
              </div>
            )}
            {!ordersLoading && ordersError && (
              <div className="oc-alert oc-alert-error" role="alert">{ordersError}</div>
            )}
            {!ordersLoading && !ordersError && (
              <>
                <p className="oc-order-summary">
                  {numberFormatter.format(
                    Number(orderListData?.totalOrders) || orderListData?.orders?.length || 0,
                  )} đơn trong khoảng {appliedFilters.fromDate} đến {appliedFilters.toDate}.
                </p>
                {orderListData?.orders?.length ? (
                  <div className="oc-order-list" aria-label="Danh sách đơn của nhóm">
                    {orderListData.orders.map((order) => (
                      <button
                        className="oc-order-row"
                        type="button"
                        key={order.orderId}
                        onClick={() => openOrderDetailModal(order)}
                      >
                        <strong>{order.orderCode || order.orderId}</strong>
                        <span>{order.orderDate || "—"}</span>
                        <span>{order.customerName || "Khách lẻ"}</span>
                        <span>{order.employeeName || order.employeeCode || "—"}</span>
                        <span>{numberFormatter.format(Number(order.cakeQuantity) || 0)} bánh</span>
                        {order.isWow ? <small className="oc-level-badge oc-level-wow">WOW</small> : <small>—</small>}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="oc-empty-search">Không có đơn trong khoảng thời gian đã chọn.</p>
                )}
              </>
            )}
          </div>
          <footer className="oc-modal-footer">
            <button
              className="oc-primary-button"
              type="button"
              disabled={ordersLoading}
              onClick={() => setOrderListGroup(null)}
            >
              Đóng
            </button>
          </footer>
        </Modal>
      )}

      {selectedOrder && (
        <Modal
          title={`Chi tiết đơn · ${selectedOrder.orderCode || selectedOrder.orderId}`}
          labelledBy="oc-order-detail-modal-title"
          onClose={() => !orderDetailLoading && setSelectedOrder(null)}
          wide
        >
          <div className="oc-modal-body">
            {orderDetailLoading && (
              <div className="oc-loading oc-order-loading" role="status">
                <FiRefreshCw className="oc-spin" /> Đang tải chi tiết đơn...
              </div>
            )}
            {!orderDetailLoading && orderDetailError && (
              <div className="oc-alert oc-alert-error" role="alert">{orderDetailError}</div>
            )}
            {!orderDetailLoading && !orderDetailError && orderDetail && (
              <>
                <div className="oc-order-detail-grid">
                  <div><span>Mã đơn</span><strong>{orderDetail.orderCode || orderDetail.orderId}</strong></div>
                  <div><span>Ngày đơn</span><strong>{orderDetail.orderDate || "—"}</strong></div>
                  <div><span>Khách hàng</span><strong>{orderDetail.customerName || "Khách lẻ"}</strong></div>
                  <div><span>Nhân viên</span><strong>{orderDetail.employeeName || orderDetail.employeeCode || "—"}</strong></div>
                  <div><span>Tổng số bánh</span><strong>{numberFormatter.format(Number(orderDetail.cakeQuantity) || 0)} bánh</strong></div>
                  <div><span>Phân loại</span><strong>{orderDetail.isWow ? "Đơn WOW" : "Đơn thường"}</strong></div>
                </div>
                <div className="oc-order-items">
                  <h3>Sản phẩm trong đơn</h3>
                  {orderDetail.items?.length ? (
                    <div className="oc-order-item-table" role="table" aria-label="Sản phẩm trong đơn">
                      <div className="oc-order-item-head" role="row">
                        <span role="columnheader">Mã sản phẩm</span>
                        <span role="columnheader">Tên sản phẩm</span>
                        <span role="columnheader">Số lượng</span>
                      </div>
                      {orderDetail.items.map((item, index) => (
                        <div className="oc-order-item-row" role="row" key={item.lineId || `${item.productCode}-${index}`}>
                          <span role="cell">{item.productCode || "—"}</span>
                          <span role="cell">{item.productName || "—"}</span>
                          <strong role="cell">{numberFormatter.format(Number(item.quantity) || 0)} {item.unit || "bánh"}</strong>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="oc-empty-search">Đơn chưa có chi tiết sản phẩm.</p>
                  )}
                </div>
              </>
            )}
          </div>
          <footer className="oc-modal-footer">
            <button
              className="oc-secondary-button"
              type="button"
              disabled={orderDetailLoading}
              onClick={() => setSelectedOrder(null)}
            >
              Quay lại danh sách
            </button>
          </footer>
        </Modal>
      )}

      {kpiGroup && (
        <Modal
          title={`KPI · ${kpiGroup.groupId} - ${kpiGroup.groupName}`}
          labelledBy="oc-kpi-modal-title"
          onClose={() => !saving && setKpiGroup(null)}
          wide
        >
          <form onSubmit={handleSaveKpi}>
            <div className="oc-modal-body">
              {modalError && <div className="oc-alert oc-alert-error" role="alert">{modalError}</div>}
              <div className="oc-form-grid oc-date-grid">
                <label className="oc-form-field">
                  <span>Từ ngày</span>
                  <input
                    type="date"
                    value={kpiForm.fromDate}
                    onChange={(event) =>
                      setKpiForm((current) => ({ ...current, fromDate: event.target.value }))
                    }
                  />
                </label>
                <label className="oc-form-field">
                  <span>Đến ngày</span>
                  <input
                    type="date"
                    value={kpiForm.toDate}
                    onChange={(event) =>
                      setKpiForm((current) => ({ ...current, toDate: event.target.value }))
                    }
                  />
                </label>
              </div>

              {[
                {
                  key: "cakeQuantity",
                  title: "Số lượng bánh",
                  note: "Nhập số bánh cho từng mốc.",
                },
                {
                  key: "wowOrderCount",
                  title: "Số đơn WOW",
                  note: "Đơn WOW là đơn có trên 100 bánh/đơn.",
                },
              ].map((metric) => (
                <fieldset className="oc-kpi-fieldset" key={metric.key}>
                  <legend>{metric.title}</legend>
                  <p>{metric.note}</p>
                  <div className="oc-milestone-inputs">
                    {MILESTONES.map((milestone) => (
                      <label className="oc-form-field" key={milestone.key}>
                        <span>{metric.title} - {milestone.label}</span>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={kpiForm[metric.key][milestone.key]}
                          onChange={(event) =>
                            updateKpiMilestone(
                              metric.key,
                              milestone.key,
                              event.target.value,
                            )
                          }
                          placeholder={milestone.label}
                        />
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
            <footer className="oc-modal-footer">
              {kpiGroup.kpi && (
                <button
                  className="oc-danger-button"
                  type="button"
                  onClick={handleDeleteKpi}
                  disabled={saving}
                >
                  <FiTrash2 /> Xóa KPI
                </button>
              )}
              <span className="oc-footer-spacer" />
              <button className="oc-secondary-button" type="button" onClick={() => setKpiGroup(null)} disabled={saving}>
                Hủy
              </button>
              <button className="oc-primary-button" type="submit" disabled={saving}>
                {saving ? "Đang lưu..." : "Lưu KPI"}
              </button>
            </footer>
          </form>
        </Modal>
      )}

      {memberGroup && (
        <Modal
          title={`Thành viên · ${memberGroup.groupId} - ${memberGroup.groupName}`}
          labelledBy="oc-member-modal-title"
          onClose={() => !saving && setMemberGroup(null)}
        >
          <div className="oc-modal-body">
            {modalError && <div className="oc-alert oc-alert-error" role="alert">{modalError}</div>}
            <form className="oc-employee-search" onSubmit={handleSearchEmployee}>
              <label className="oc-form-field">
                <span>Mã nhân viên (nhiều mã ngăn cách bằng dấu phẩy)</span>
                <div className="oc-search-control">
                  <input
                    value={employeeCode}
                    onChange={(event) => {
                      setEmployeeCode(event.target.value.toUpperCase());
                      setEmployeeResults([]);
                      setHasSearchedEmployee(false);
                      setModalError("");
                    }}
                    placeholder="Ví dụ: NV201 hoặc NV201, NV202, NV203"
                    autoFocus
                  />
                  <button
                    className="oc-primary-button"
                    type="submit"
                    disabled={searching || saving}
                  >
                    {enteredEmployeeCodes.length > 1 ? <FiUserPlus /> : <FiSearch />}
                    {enteredEmployeeCodes.length > 1
                      ? saving
                        ? "Đang thêm..."
                        : `Thêm ${enteredEmployeeCodes.length} mã`
                      : searching
                        ? "Đang tìm..."
                        : "Tìm"}
                  </button>
                </div>
              </label>
            </form>

            {employeeResults.length > 0 && (
              <div className="oc-search-results" aria-label="Kết quả tìm nhân viên">
                {employeeResults.map((employee) => {
                  const assignedGroupId = employee.assignedGroupId ?? null;
                  const alreadyInGroup = assignedGroupId === memberGroup.groupId;
                  return (
                    <div className="oc-person-row" key={employee.employeeCode}>
                      <div>
                        <strong>{employee.employeeCode}</strong>
                        <span>{employee.employeeName}</span>
                        {assignedGroupId !== null && !alreadyInGroup && (
                          <small>Đang thuộc nhóm {assignedGroupId}</small>
                        )}
                      </div>
                      <button
                        className="oc-secondary-button"
                        type="button"
                        disabled={saving || alreadyInGroup}
                        onClick={() => handleAddMember(employee)}
                      >
                        {alreadyInGroup ? "Đã trong nhóm" : assignedGroupId !== null ? "Chuyển vào nhóm" : "Thêm vào nhóm"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {hasSearchedEmployee && !searching && employeeResults.length === 0 && !modalError && (
              <p className="oc-empty-search">Không tìm thấy nhân viên phù hợp.</p>
            )}

            <div className="oc-current-members">
              <h3>Thành viên hiện tại ({memberGroup.members?.length || 0})</h3>
              {memberGroup.members?.length ? (
                memberGroup.members.map((member) => (
                  <div className="oc-person-row" key={member.employeeCode}>
                    <div>
                      <strong>{member.employeeCode}</strong>
                      <span>{member.employeeName}</span>
                    </div>
                    <button
                      className="oc-icon-button oc-remove-member"
                      type="button"
                      aria-label={`Gỡ ${member.employeeCode} khỏi nhóm`}
                      disabled={saving}
                      onClick={() => handleRemoveMember(member.employeeCode)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))
              ) : (
                <p className="oc-empty-search">Nhóm chưa có thành viên.</p>
              )}
            </div>
          </div>
          <footer className="oc-modal-footer">
            <button className="oc-primary-button" type="button" onClick={() => setMemberGroup(null)} disabled={saving}>
              Hoàn tất
            </button>
          </footer>
        </Modal>
      )}
      </main>
    </>
  );
};

export default OfficeCompetitionPage;
